import { useMemo, useState } from "react";
import { Button, ConfirmDialog, useToast } from "@a2z/ui";
import type { SellerPermission, StoreRoleView } from "@/lib/api/types";
import { useStoreRolesQuery } from "@/modules/team/services/useStoreRolesQuery";
import { useStoreMembersQuery } from "@/modules/team/services/useStoreMembersQuery";
import { useRolePermissionsQuery } from "@/modules/team/services/useRolePermissionsQuery";
import { useCreateStoreRoleMutation } from "@/modules/team/services/useCreateStoreRoleMutation";
import { useUpdateStoreRoleMutation } from "@/modules/team/services/useUpdateStoreRoleMutation";
import { useDeleteStoreRoleMutation } from "@/modules/team/services/useDeleteStoreRoleMutation";
import { useHasPermission } from "@/modules/team/hooks/useHasPermission";
import { useSellerStoreSlug } from "@/lib/routing/useSellerStoreSlug";
import { useIsMobile } from "@/shared/hooks/useIsMobile";
import { useTranslations, useLocale } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import {
  Card,
  Icons,
  ModalShell,
  tokens,
} from "@/modules/settings/components/redesign/store-settings/primitives";
import {
  permissionGroupLabel,
  permissionGroupOf,
  permissionLabel,
  systemRoleDesc,
  systemRoleName,
  uiText,
} from "./rbac-labels";
import { buildAreas } from "./permission-tiers";
import { AreaAccessRow } from "./AreaAccessRow";

const ps = keys.seller.profileSettings;

function extractErrorMessage(error: unknown): string {
  if (!error) return "";
  if (error instanceof Error) return error.message;
  return String(error);
}

// Derive the internal SCREAMING_SNAKE_CASE key (a machine identifier the seller
// never sees). Arabic names yield no Latin chars, so fall back to a stable
// random ROLE_<8 hex> key — the server requires /^[A-Z][A-Z0-9_]{1,31}$/.
function nameToKey(name: string): string {
  const derived = name
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 32);
  if (/^[A-Z][A-Z0-9_]{1,31}$/.test(derived)) return derived;
  // Empty / non-Latin / starts-with-digit — generate a stable fallback.
  const bytes = new Uint8Array(4);
  crypto.getRandomValues(bytes);
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  return `ROLE_${hex.toUpperCase()}`;
}

// Group a set of permissions by their category (first enum segment), preserving
// the canonical order in which they arrive.
function groupPermissions(
  permissions: SellerPermission[],
): Array<{ group: string; items: SellerPermission[] }> {
  const buckets = new Map<string, SellerPermission[]>();
  for (const permission of permissions) {
    const group = permissionGroupOf(permission);
    const bucket = buckets.get(group);
    if (bucket) bucket.push(permission);
    else buckets.set(group, [permission]);
  }
  return [...buckets.entries()].map(([group, items]) => ({ group, items }));
}

// Localized name/description for a role: system roles use the localized catalog,
// custom roles use the seller-entered text.
function useRoleText() {
  const locale = useLocale();
  const t = useTranslations();
  return (role: StoreRoleView) => ({
    name: role.isSystem
      ? systemRoleName(role.key, locale, role.name)
      : role.name,
    desc: role.isSystem
      ? systemRoleDesc(role.key, locale, role.description)
      : (role.description ??
        t(ps.permissionsCount, { count: role.permissions.length })),
  });
}

export function RolesPanel() {
  const t = useTranslations();
  const storeSlug = useSellerStoreSlug();
  const has = useHasPermission(storeSlug);

  const { data: roles = [], isLoading: rolesLoading } =
    useStoreRolesQuery(storeSlug);
  const { data: allPermissions = [] } = useRolePermissionsQuery(storeSlug);
  const { data: members = [] } = useStoreMembersQuery(storeSlug);

  const deleteRole = useDeleteStoreRoleMutation();

  const [editing, setEditing] = useState<StoreRoleView | "new" | null>(null);
  const [viewing, setViewing] = useState<StoreRoleView | null>(null);
  // Role pending deletion — drives the confirm dialog (replaces native confirm).
  const [deleting, setDeleting] = useState<StoreRoleView | null>(null);

  // How many members currently hold a role — surfaced in the delete confirm so
  // the seller knows the impact (the server also blocks delete-in-use).
  const memberCountForRole = (roleId: string): number =>
    members.filter((member) => member.roles.some((role) => role.id === roleId))
      .length;

  const confirmDelete = async (): Promise<void> => {
    if (!deleting) return;
    try {
      await deleteRole.mutateAsync({ storeSlug, roleId: deleting.id });
      setDeleting(null);
    } catch {
      // Error is surfaced globally via the React Query MutationCache handler.
    }
  };

  const canCreate = has("ROLE_CREATE");
  const canEdit = has("ROLE_EDIT");
  const canDelete = has("ROLE_DELETE");

  const systemRoles = roles.filter((role) => role.isSystem);
  const customRoles = roles.filter((role) => !role.isSystem);

  return (
    <Card
      title={t(ps.rolesPermsTitle)}
      desc={t(ps.rolesPermsDesc)}
      action={
        canCreate ? (
          <Button
            variant="link"
            size="sm"
            onClick={() => setEditing("new")}
            trailingIcon={<span style={{ fontSize: 14 }}>{"+"}</span>}
          >
            {t(ps.newRole)}
          </Button>
        ) : undefined
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <RolesSection
          title={t(ps.defaultRoles)}
          hint={t(ps.defaultRolesHint)}
          roles={systemRoles}
          loading={rolesLoading}
          canEdit={false}
          canDelete={false}
          deletingId={null}
          onEdit={() => undefined}
          onDelete={() => undefined}
          onView={(role) => setViewing(role)}
        />

        <RolesSection
          title={t(ps.customRoles)}
          hint={
            canCreate ? t(ps.customRolesHintCreate) : t(ps.customRolesHintEmpty)
          }
          roles={customRoles}
          loading={rolesLoading}
          canEdit={canEdit}
          canDelete={canDelete}
          deletingId={deleteRole.isPending ? (deleting?.id ?? null) : null}
          onEdit={(role) => setEditing(role)}
          onDelete={(role) => setDeleting(role)}
          onView={(role) => setViewing(role)}
          onCreate={canCreate ? () => setEditing("new") : undefined}
        />
      </div>

      {editing ? (
        <RoleEditorModal
          storeSlug={storeSlug}
          allPermissions={allPermissions}
          initialRole={editing === "new" ? null : editing}
          templates={systemRoles}
          onClose={() => setEditing(null)}
        />
      ) : null}

      {viewing ? (
        <RolePermissionsModal role={viewing} onClose={() => setViewing(null)} />
      ) : null}

      <ConfirmDialog
        isOpen={deleting !== null}
        title={t(keys.common.delete)}
        body={
          deleting
            ? [
                t(ps.deleteRoleConfirm, { name: deleting.name }),
                memberCountForRole(deleting.id) > 0
                  ? t(ps.roleInUseWarning, {
                      count: memberCountForRole(deleting.id),
                    })
                  : "",
              ]
                .filter(Boolean)
                .join(" ")
            : ""
        }
        confirmLabel={t(keys.common.delete)}
        cancelLabel={t(keys.common.cancel)}
        isSubmitting={deleteRole.isPending}
        intent="danger"
        onConfirm={() => void confirmDelete()}
        onCancel={() => setDeleting(null)}
      />
    </Card>
  );
}

type RolesSectionProps = {
  title: string;
  hint: string;
  roles: StoreRoleView[];
  loading: boolean;
  canEdit: boolean;
  canDelete: boolean;
  deletingId: string | null;
  onEdit: (role: StoreRoleView) => void;
  onDelete: (role: StoreRoleView) => void;
  onView: (role: StoreRoleView) => void;
  onCreate?: () => void;
};

function RolesSection({
  title,
  hint,
  roles,
  loading,
  canEdit,
  canDelete,
  deletingId,
  onEdit,
  onDelete,
  onView,
  onCreate,
}: RolesSectionProps) {
  const t = useTranslations();
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <div>
        <div
          style={{
            fontSize: 10.5,
            fontWeight: 600,
            letterSpacing: 0.8,
            textTransform: "uppercase",
            color: tokens.text3,
          }}
        >
          {title}
        </div>
        <div style={{ fontSize: 11.5, color: tokens.text3, marginTop: 2 }}>
          {hint}
        </div>
      </div>
      {loading ? (
        <div style={{ fontSize: 12.5, color: tokens.text3 }}>
          {t(ps.loading)}
        </div>
      ) : roles.length === 0 ? (
        <EmptyRoles label={hint} onCreate={onCreate} createLabel={t(ps.newRole)} />
      ) : (
        roles.map((role) => (
          <RoleRow
            key={role.id}
            role={role}
            canEdit={canEdit}
            canDelete={canDelete}
            busy={deletingId === role.id}
            onEdit={() => onEdit(role)}
            onDelete={() => onDelete(role)}
            onView={() => onView(role)}
          />
        ))
      )}
    </div>
  );
}

function EmptyRoles({
  label,
  onCreate,
  createLabel,
}: {
  label: string;
  onCreate?: () => void;
  createLabel: string;
}) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 10,
        padding: "20px 16px",
        borderRadius: 10,
        border: `1px dashed ${tokens.border}`,
        background: tokens.bgSoft,
        textAlign: "center",
      }}
    >
      <span style={{ color: tokens.text3, display: "flex" }}>{Icons.shield}</span>
      <span style={{ fontSize: 12, color: tokens.text3, maxWidth: 320 }}>
        {label}
      </span>
      {onCreate ? (
        <Button variant="outline" size="sm" onClick={onCreate}>
          {createLabel}
        </Button>
      ) : null}
    </div>
  );
}

type RoleRowProps = {
  role: StoreRoleView;
  canEdit: boolean;
  canDelete: boolean;
  busy?: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onView: () => void;
};

function RoleRow({
  role,
  canEdit,
  canDelete,
  busy = false,
  onEdit,
  onDelete,
  onView,
}: RoleRowProps) {
  const t = useTranslations();
  const text = useRoleText()(role);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onView}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onView();
        }
      }}
      style={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: 12,
        rowGap: 8,
        padding: "10px 12px",
        borderRadius: 10,
        border: `1px solid ${tokens.border}`,
        background: tokens.surface,
        cursor: "pointer",
        transition: "background 0.12s, border-color 0.12s",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = tokens.bgSoft;
        e.currentTarget.style.borderColor = tokens.borderStrong;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = tokens.surface;
        e.currentTarget.style.borderColor = tokens.border;
      }}
    >
      <div style={{ flex: 1, minWidth: "min(160px, 100%)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontSize: 13.5, fontWeight: 600, color: tokens.text }}>
            {text.name}
          </span>
          {role.isSystem ? (
            <span
              style={{
                fontSize: 10,
                fontWeight: 600,
                padding: "1px 5px",
                borderRadius: 4,
                background: tokens.bgSoft,
                color: tokens.text3,
                textTransform: "uppercase",
                letterSpacing: 0.4,
              }}
            >
              {t(ps.system)}
            </span>
          ) : null}
        </div>
        {text.desc ? (
          <div
            style={{
              fontSize: 11.5,
              color: tokens.text3,
              marginTop: 1,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {text.desc}
          </div>
        ) : null}
      </div>

      {/* Compact permission-count chip — tells the seller how much a role grants. */}
      <span
        style={{
          fontSize: 11,
          fontWeight: 600,
          padding: "3px 8px",
          borderRadius: 999,
          background: tokens.bgSoft,
          color: tokens.text2,
          flexShrink: 0,
          whiteSpace: "nowrap",
        }}
      >
        {t(ps.permissionsCount, { count: role.permissions.length })}
      </span>

      {!role.isSystem && canEdit ? (
        <Button
          variant="outline"
          size="sm"
          className="max-lg:min-h-10"
          onClick={(e) => {
            e.stopPropagation();
            onEdit();
          }}
        >
          {t(keys.common.edit)}
        </Button>
      ) : null}
      {!role.isSystem && canDelete ? (
        <Button
          variant="outline"
          size="sm"
          disabled={busy}
          className="max-lg:min-h-10"
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          style={{
            color: tokens.danger,
            borderColor: "oklch(0.85 0.08 25)",
          }}
        >
          {t(keys.common.delete)}
        </Button>
      ) : null}
    </div>
  );
}

// Read-only viewer for a role's permissions, grouped and localized. Lets a
// seller understand what a (system) role can actually do.
function RolePermissionsModal({
  role,
  onClose,
}: {
  role: StoreRoleView;
  onClose: () => void;
}) {
  const t = useTranslations();
  const locale = useLocale();
  const text = useRoleText()(role);
  const grouped = useMemo(() => groupPermissions(role.permissions), [role]);

  return (
    <ModalShell
      icon={Icons.shield}
      title={text.name}
      subtitle={text.desc ?? undefined}
      onClose={onClose}
      onSave={onClose}
      valid
      saveLabel={t(keys.common.close)}
      maxWidth={560}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {grouped.length === 0 ? (
          <div style={{ fontSize: 12.5, color: tokens.text3 }}>
            {t(ps.selectOnePermission)}
          </div>
        ) : (
          grouped.map((bucket) => (
            <div
              key={bucket.group}
              style={{ display: "flex", flexDirection: "column", gap: 6 }}
            >
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: tokens.text2,
                }}
              >
                {permissionGroupLabel(bucket.group, locale)}
              </div>
              <div
                style={{ display: "flex", flexWrap: "wrap", gap: 6 }}
              >
                {bucket.items.map((permission) => (
                  <span
                    key={permission}
                    style={{
                      fontSize: 11.5,
                      padding: "3px 8px",
                      borderRadius: 999,
                      background: tokens.bgSoft,
                      color: tokens.text2,
                      border: `1px solid ${tokens.border}`,
                    }}
                  >
                    {permissionLabel(permission, locale)}
                  </span>
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </ModalShell>
  );
}

type RoleEditorModalProps = {
  storeSlug: string;
  allPermissions: SellerPermission[];
  initialRole: StoreRoleView | null;
  templates: StoreRoleView[];
  onClose: () => void;
};

function RoleEditorModal({
  storeSlug,
  allPermissions,
  initialRole,
  templates,
  onClose,
}: RoleEditorModalProps) {
  const t = useTranslations();
  const locale = useLocale();
  const isMobile = useIsMobile();
  const isEdit = initialRole !== null;
  const [name, setName] = useState(initialRole?.name ?? "");
  const [description, setDescription] = useState(
    initialRole?.description ?? "",
  );
  // The internal key is auto-derived from the name and never shown to the
  // seller (it's a machine identifier, not product UI).
  const [key, setKey] = useState(initialRole?.key ?? "");
  const [permissions, setPermissions] = useState<Set<SellerPermission>>(
    () => new Set(initialRole?.permissions ?? []),
  );

  const create = useCreateStoreRoleMutation();
  const update = useUpdateStoreRoleMutation();
  const pending = create.isPending || update.isPending;
  const editorToast = useToast();

  const handleNameChange = (next: string): void => {
    setName(next);
    if (!isEdit) setKey(nameToKey(next));
  };

  const areas = useMemo(() => buildAreas(allPermissions), [allPermissions]);

  const valid = name.trim().length > 0 && permissions.size > 0;

  const grantsRoleArea =
    permissions.has("ROLE_CREATE") ||
    permissions.has("ROLE_EDIT") ||
    permissions.has("ROLE_DELETE");

  const submit = async () => {
    try {
      if (isEdit && initialRole) {
        await update.mutateAsync({
          storeSlug,
          roleId: initialRole.id,
          name: name.trim(),
          description: description.trim() || null,
          permissions: [...permissions],
        });
      } else {
        await create.mutateAsync({
          storeSlug,
          key: key || nameToKey(name),
          name: name.trim(),
          description: description.trim() || undefined,
          permissions: [...permissions],
        });
      }
      onClose();
    } catch (caught) {
      // The seller never sees the internal key, so any SCREAMING_SNAKE_CASE
      // error from the server is meaningless to them. Always show a clean,
      // user-facing message via toast; keep the modal open for correction.
      const raw = extractErrorMessage(caught);
      const friendly = /SCREAMING_SNAKE_CASE|key must|^key /i.test(raw)
        ? t(keys.errors.generic)
        : raw || t(keys.errors.generic);
      editorToast.error(friendly);
    }
  };

  const fieldStyle = {
    padding: "10px 12px",
    borderRadius: 8,
    border: `1px solid ${tokens.border}`,
    background: tokens.surface,
    color: tokens.text,
    fontSize: 13,
  } as const;

  const chipStyle = {
    padding: "6px 12px",
    borderRadius: 999,
    border: `1px solid ${tokens.border}`,
    background: tokens.surface,
    color: tokens.text2,
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
  } as const;

  return (
    <ModalShell
      icon={Icons.shield}
      title={
        isEdit ? t(ps.editRole, { name: initialRole?.name ?? "" }) : t(ps.newRole)
      }
      subtitle={t(ps.roleEditorSubtitle)}
      onClose={onClose}
      onSave={submit}
      valid={valid && !pending}
      saveLabel={
        pending
          ? t(ps.saving)
          : isEdit
            ? t(keys.seller.storeSettings.saveChanges)
            : t(ps.createRole)
      }
      maxWidth={isMobile ? 720 : 1040}
    >
      <div
        style={{
          display: isMobile ? "flex" : "grid",
          flexDirection: isMobile ? "column" : undefined,
          // Desktop: form column FIRST in source → RTL start (right) = form,
          // RTL end (left) = permissions. Form has a fixed width; permissions
          // take the rest.
          gridTemplateColumns: isMobile ? undefined : "360px minmax(0, 1fr)",
          gap: isMobile ? 16 : 24,
          // Desktop: bound the grid so the permissions column can scroll
          // inside while the form column stays put. Mobile: no inner scroll
          // — the whole modal scrolls as one surface.
          height: isMobile ? undefined : "calc(80vh - 140px)",
          minHeight: 0,
        }}
      >
        {/* Form column (RTL start / right; LTR start / left). Always visible. */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 14,
            // On desktop this column doesn't scroll — it just sits there.
            overflow: "visible",
          }}
        >
          <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <span
              style={{ fontSize: 12, fontWeight: 600, color: tokens.text2 }}
            >
              {t(ps.nameLabel)}
            </span>
            <input
              type="text"
              value={name}
              onChange={(event) => handleNameChange(event.target.value)}
              placeholder={t(ps.namePlaceholder)}
              autoFocus
              maxLength={60}
              style={fieldStyle}
            />
          </label>

          <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <span
              style={{ fontSize: 12, fontWeight: 600, color: tokens.text2 }}
            >
              {t(ps.descriptionLabel)}{" "}
              <span style={{ color: tokens.text3 }}>{t(ps.optional)}</span>
            </span>
            <textarea
              value={description ?? ""}
              onChange={(event) => setDescription(event.target.value)}
              placeholder={t(ps.descriptionPlaceholder)}
              rows={2}
              maxLength={200}
              style={{ ...fieldStyle, resize: "vertical" }}
            />
          </label>

          {/* Templates — start a new role from an existing (system) role. */}
          {!isEdit && templates.length > 0 ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <span
                style={{ fontSize: 12, fontWeight: 600, color: tokens.text2 }}
              >
                {uiText("startFrom", locale)}
              </span>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {templates.map((template) => (
                  <button
                    key={template.id}
                    type="button"
                    onClick={() =>
                      setPermissions(new Set(template.permissions))
                    }
                    style={chipStyle}
                  >
                    {systemRoleName(template.key, locale, template.name)}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => setPermissions(new Set())}
                  style={chipStyle}
                >
                  {uiText("fromScratch", locale)}
                </button>
              </div>
            </div>
          ) : null}
        </div>

        {/* Permissions column (RTL end / left; LTR end / right). The only
            scrolling surface on desktop. */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 10,
            minWidth: 0,
            minHeight: 0,
            overflowY: isMobile ? "visible" : "auto",
            paddingInlineEnd: isMobile ? 0 : 4,
          }}
        >
          <span style={{ fontSize: 12, fontWeight: 600, color: tokens.text2 }}>
            {t(ps.permissionsCountLabel, {
              selected: permissions.size,
              total: allPermissions.length,
            })}
          </span>

          {permissions.size === 0 ? (
            <div
              style={{
                fontSize: 11.5,
                color: tokens.text3,
                padding: "8px 10px",
                borderRadius: 8,
                background: tokens.bgSoft,
                border: `1px solid ${tokens.border}`,
              }}
            >
              {uiText("leastPrivilegeHint", locale)}
            </div>
          ) : null}

          {/* One access-tier row per area. */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 8,
            }}
          >
            {areas.map((area) => (
              <AreaAccessRow
                key={area.group}
                area={area}
                selected={permissions}
                onChange={setPermissions}
              />
            ))}
          </div>

          {grantsRoleArea ? (
            <div style={{ fontSize: 11, color: tokens.danger, lineHeight: 1.5 }}>
              {uiText("rolesAreaWarning", locale)}
            </div>
          ) : null}
          {permissions.size === 0 ? (
            <span style={{ fontSize: 11, color: tokens.danger }}>
              {t(ps.selectOnePermission)}
            </span>
          ) : null}
        </div>
      </div>
    </ModalShell>
  );
}
